import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type InvokeBackstageActionOptions<TInput extends object> = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  pluginId: string;
  actionName: string;
  input?: TInput;
};

type InvokeBackstageActionResponse<TOutput> = {
  output?: TOutput;
};

type BackstageActionRequestOptions<TInput extends object> = Omit<
  InvokeBackstageActionOptions<TInput>,
  'discoveryApi' | 'fetchApi'
>;

export interface BackstageActionsApi {
  invokeBackstageAction<TOutput, TInput extends object = Record<string, never>>(
    options: BackstageActionRequestOptions<TInput>,
  ): Promise<TOutput>;
}

export const BackstageActionsApiRef = createApiRef<BackstageActionsApi>({
  id: 'plugin.devkit.backstage-actions.service',
});

export class BackstageActionsService implements BackstageActionsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  invokeBackstageAction<TOutput, TInput extends object = Record<string, never>>({
    pluginId,
    actionName,
    input,
  }: BackstageActionRequestOptions<TInput>): Promise<TOutput> {
    return invokeBackstageAction<TOutput, TInput>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      pluginId,
      actionName,
      input,
    });
  }
}

export async function invokeBackstageAction<TOutput, TInput extends object = Record<string, never>>({
  discoveryApi,
  fetchApi,
  pluginId,
  actionName,
  input,
}: InvokeBackstageActionOptions<TInput>): Promise<TOutput> {
  const baseUrl = await discoveryApi.getBaseUrl(pluginId);
  const actionId = `${pluginId}:${actionName}`;
  const response = await fetchApi.fetch(
    `${baseUrl}/.backstage/actions/v1/actions/${encodeURIComponent(actionId)}/invoke`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input ?? {}),
    },
  );

  if (!response.ok) {
    throw new Error(`Unable to invoke action ${actionId} (${response.status})`);
  }

  const body = (await response.json()) as InvokeBackstageActionResponse<TOutput>;
  if (body.output === undefined) {
    throw new Error(`Action ${actionId} returned no output`);
  }

  return body.output;
}
